import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Accordion, AccordionSummary,
  AccordionDetails, Stepper, Step, StepLabel, StepContent, Divider,
  List, ListItem, ListItemIcon, ListItemText, Chip, Alert, Card, CardContent,
  Grid, Tab, Tabs
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useStore } from '../context/StoreContext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PaymentIcon from '@mui/icons-material/Payment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import GavelIcon from '@mui/icons-material/Gavel';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import VerifiedIcon from '@mui/icons-material/Verified';

// Default company data values (not UI labels)
const COMPANY_INFO = {
  name: '昀美國際貿易有限公司',
  taxId: '42762120',
  phone: '04-7380668',
  address: '彰化市彰南路三段481-1號',
  email: 'service@urstore.tw',
  lineId: '@454ygmfg',
  serviceHours: '週一至週五 9:00-18:00'
};

const ShoppingGuide = () => {
  const { t } = useTranslation('common');
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(0);
  const { shippingConfig } = useStore();
  const baseShippingFee = Number(shippingConfig?.base_fee ?? 60);
  const freeShippingThreshold = Number(shippingConfig?.free_shipping_threshold ?? 799);
  const homeShippingFee = Math.max(baseShippingFee + 40, baseShippingFee);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab !== null) {
      const tabIndex = parseInt(tab);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 5) {
        setTabValue(tabIndex);
      }
    }
  }, [searchParams]);

  const handleTabChange = (e, newValue) => {
    setTabValue(newValue);
    setSearchParams({ tab: newValue.toString() });
  };

  const shoppingSteps = [
    { label: t('guide.step1_label'), description: t('guide.step1_desc') },
    { label: t('guide.step2_label'), description: t('guide.step2_desc') },
    { label: t('guide.step3_label'), description: t('guide.step3_desc') },
    { label: t('guide.step4_label'), description: t('guide.step4_desc') },
    { label: t('guide.step5_label'), description: t('guide.step5_desc') },
    { label: t('guide.step6_label'), description: t('guide.step6_desc') }
  ];

  const paymentMethods = [
    { name: t('guide.pay_cvs'), fee: '$0', desc: t('guide.pay_cvs_desc'), time: t('guide.pay_cvs_time') },
    { name: t('guide.pay_linepay'), fee: '$0', desc: t('guide.pay_linepay_desc'), time: t('guide.pay_linepay_time') },
    { name: t('guide.pay_credit'), fee: '$0', desc: t('guide.pay_credit_desc'), time: t('guide.pay_credit_time') },
    { name: t('guide.pay_code'), fee: '$0', desc: t('guide.pay_code_desc'), time: t('guide.pay_code_time') }
  ];

  const shippingMethods = [
    { name: t('guide.ship_711'), fee: `$${baseShippingFee}`, freeThreshold: `$${freeShippingThreshold}`, time: t('guide.ship_711_time'), note: t('guide.ship_711_note') },
    { name: t('guide.ship_family'), fee: `$${baseShippingFee}`, freeThreshold: `$${freeShippingThreshold}`, time: t('guide.ship_family_time'), note: t('guide.ship_family_note') },
    { name: t('guide.ship_home'), fee: `$${homeShippingFee}`, freeThreshold: `$${freeShippingThreshold}`, time: t('guide.ship_home_time'), note: t('guide.ship_home_note') }
  ];

  const faqItems = [
    { question: t('guide.faq1_q'), answer: t('guide.faq1_a') },
    { question: t('guide.faq2_q'), answer: t('guide.faq2_a') },
    { question: t('guide.faq3_q'), answer: t('guide.faq3_a') },
    { question: t('guide.faq4_q'), answer: t('guide.faq4_a') },
    { question: t('guide.faq5_q'), answer: t('guide.faq5_a') },
    { question: t('guide.faq6_q'), answer: t('guide.faq6_a') }
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        {t('guide.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('guide.subtitle')}
      </Typography>

      <Paper sx={{ mb: 4 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab icon={<BusinessIcon />} label={t('guide.tab_about')} />
          <Tab icon={<ShoppingCartIcon />} label={t('guide.tab_flow')} />
          <Tab icon={<PaymentIcon />} label={t('guide.tab_payment')} />
          <Tab icon={<AssignmentReturnIcon />} label={t('guide.tab_return')} />
          <Tab icon={<GavelIcon />} label={t('guide.tab_terms')} />
          <Tab icon={<PrivacyTipIcon />} label={t('guide.tab_privacy')} />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" />
            {t('guide.about_title')}
          </Typography>
          <Alert severity="success" icon={<VerifiedIcon />} sx={{ my: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">{t('guide.about_verified_title')}</Typography>
            {t('guide.about_verified_desc')}
          </Alert>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">{t('guide.about_intro_title')}</Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>{t('guide.about_intro_p1')}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('guide.about_intro_p2')}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: '#f5f5f5' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">{t('guide.about_company_title')}</Typography>
                  <List dense>
                    <ListItem><ListItemIcon><BusinessIcon color="primary" /></ListItemIcon><ListItemText primary={t('guide.label_company_name')} secondary={COMPANY_INFO.name} /></ListItem>
                    <ListItem><ListItemIcon><VerifiedIcon color="success" /></ListItemIcon><ListItemText primary={t('guide.label_tax_id')} secondary={COMPANY_INFO.taxId} /></ListItem>
                    <ListItem><ListItemIcon><PhoneIcon color="primary" /></ListItemIcon><ListItemText primary={t('guide.label_phone')} secondary={COMPANY_INFO.phone} /></ListItem>
                    <ListItem><ListItemIcon><LocationOnIcon color="primary" /></ListItemIcon><ListItemText primary={t('guide.label_address')} secondary={COMPANY_INFO.address} /></ListItem>
                    <ListItem><ListItemIcon><EmailIcon color="primary" /></ListItemIcon><ListItemText primary={t('guide.label_email')} secondary={COMPANY_INFO.email} /></ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Divider sx={{ my: 4 }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>{t('guide.about_advantages')}</Typography>
          <Grid container spacing={2}>
            {[
              { title: t('guide.adv1_title'), desc: t('guide.adv1_desc') },
              { title: t('guide.adv2_title'), desc: t('guide.adv2_desc') },
              { title: t('guide.adv3_title'), desc: t('guide.adv3_desc') },
              { title: t('guide.adv4_title'), desc: t('guide.adv4_desc') }
            ].map((item, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                  <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>{item.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {tabValue === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon color="primary" />
            {t('guide.flow_title')}
          </Typography>
          <Stepper orientation="vertical" sx={{ mt: 3 }}>
            {shoppingSteps.map((step) => (
              <Step key={step.label} active={true}>
                <StepLabel><Typography variant="subtitle1" fontWeight="bold">{step.label}</Typography></StepLabel>
                <StepContent><Typography color="text.secondary">{step.description}</Typography></StepContent>
              </Step>
            ))}
          </Stepper>
          <Divider sx={{ my: 4 }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>{t('guide.faq_section_title')}</Typography>
          {faqItems.map((faq, index) => (
            <Accordion key={index}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight="medium">{faq.question}</Typography></AccordionSummary>
              <AccordionDetails><Typography color="text.secondary">{faq.answer}</Typography></AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      )}

      {tabValue === 2 && (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PaymentIcon color="primary" />
              {t('guide.payment_title')}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {paymentMethods.map((method, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">{method.name}</Typography>
                        <Chip label={t('guide.fee_label', { fee: method.fee })} color="success" size="small" />
                      </Box>
                      <Typography variant="body2" color="text.secondary">{method.desc}</Typography>
                      <Typography variant="caption" color="primary">{t('guide.payment_time_label', { time: method.time })}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShippingIcon color="primary" />
              {t('guide.shipping_title')}
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>{t('guide.free_shipping_notice', { cvs: freeShippingThreshold, home: freeShippingThreshold })}</Alert>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {shippingMethods.map((method, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold">{method.name}</Typography>
                      <Box sx={{ my: 1 }}>
                        <Chip label={t('guide.ship_fee_label', { fee: method.fee })} size="small" sx={{ mr: 1 }} />
                        <Chip label={t('guide.ship_free_label', { threshold: method.freeThreshold })} color="error" size="small" />
                      </Box>
                      <Typography variant="body2" color="text.secondary">{t('guide.delivery_time_label', { time: method.time })}</Typography>
                      <Typography variant="caption" color="warning.main">{method.note}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>
      )}

      {tabValue === 3 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentReturnIcon color="primary" />
            {t('guide.return_title')}
          </Typography>
          <Alert severity="warning" sx={{ my: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">{t('guide.return_notice_title')}</Typography>
            {t('guide.return_notice_desc')}
          </Alert>
          <Typography variant="h6" fontWeight="bold" sx={{ mt: 3, mb: 2 }}>{t('guide.return_law_title')}</Typography>
          <List>
            <ListItem><ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon><ListItemText primary={t('guide.return_7day_title')} secondary={t('guide.return_7day_desc')} /></ListItem>
            <ListItem><ListItemIcon><WarningIcon color="warning" /></ListItemIcon><ListItemText primary={t('guide.return_not_trial_title')} secondary={t('guide.return_not_trial_desc')} /></ListItem>
          </List>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>{t('guide.return_exceptions_title')}</Typography>
          <Typography variant="body2" color="text.secondary" paragraph>{t('guide.return_exceptions_desc')}</Typography>
          <List dense>
            {(t('guide.return_exceptions_list', { returnObjects: true }) || []).map((item, index) => (
              <ListItem key={index}><ListItemIcon><InfoIcon fontSize="small" color="disabled" /></ListItemIcon><ListItemText primary={item} /></ListItem>
            ))}
          </List>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>{t('guide.defect_title')}</Typography>
          <Typography variant="body2" color="text.secondary" paragraph>{t('guide.defect_desc')}</Typography>
          <List dense>
            <ListItem><ListItemIcon><CheckCircleIcon color="primary" fontSize="small" /></ListItemIcon><ListItemText primary={t('guide.defect_item1')} /></ListItem>
            <ListItem><ListItemIcon><CheckCircleIcon color="primary" fontSize="small" /></ListItemIcon><ListItemText primary={t('guide.defect_item2')} /></ListItem>
            <ListItem><ListItemIcon><CheckCircleIcon color="primary" fontSize="small" /></ListItemIcon><ListItemText primary={t('guide.defect_item3')} /></ListItem>
          </List>
          <Typography variant="body2" color="text.secondary">{t('guide.defect_resolution')}</Typography>
        </Paper>
      )}

      {tabValue === 4 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GavelIcon color="primary" />
            {t('guide.terms_title')}
          </Typography>
          <Alert severity="info" sx={{ my: 2 }}>{t('guide.terms_notice')}</Alert>
          {['s1', 's2', 's3'].map((key) => (
            <Accordion key={key} defaultExpanded={key === 's1'}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight="bold">{t(`guide.terms_${key}_title`)}</Typography></AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {(t(`guide.terms_${key}_items`, { returnObjects: true }) || []).map((item, idx) => (
                    <ListItem key={idx}><ListItemText primary={item} /></ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight="bold">{t('guide.terms_s4_title')}</Typography></AccordionSummary>
            <AccordionDetails><Typography variant="body2" color="text.secondary">{t('guide.terms_s4_content')}</Typography></AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight="bold">{t('guide.terms_s5_title')}</Typography></AccordionSummary>
            <AccordionDetails>
              <List dense>
                {(t('guide.terms_s5_items', { returnObjects: true }) || []).map((item, idx) => (
                  <ListItem key={idx}><ListItemText primary={item} /></ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('guide.label_company_name')}: {COMPANY_INFO.name}<br />
              {t('guide.label_tax_id')}: {COMPANY_INFO.taxId}<br />
              {t('guide.label_phone')}: {COMPANY_INFO.phone}<br />
              {t('guide.label_address')}: {COMPANY_INFO.address}<br />
              LINE {t('guide.label_cs')}: {COMPANY_INFO.lineId}{t('guide.label_line_consult')}<br />
              {t('guide.label_email')}: {COMPANY_INFO.email}<br />
              {t('guide.label_service_hours')}: {COMPANY_INFO.serviceHours}
            </Typography>
          </Box>
        </Paper>
      )}

      {tabValue === 5 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PrivacyTipIcon color="primary" />
            {t('guide.privacy_title')}
          </Typography>
          <Alert severity="success" icon={<SecurityIcon />} sx={{ my: 2 }}>{t('guide.privacy_notice')}</Alert>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight="bold">{t('guide.priv_s1_title')}</Typography></AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" paragraph>{t('guide.priv_s1_intro')}</Typography>
              <List dense>
                {(t('guide.priv_s1_items', { returnObjects: true }) || []).map((item, idx) => (
                  <ListItem key={idx}><ListItemIcon><CheckCircleIcon fontSize="small" color="primary" /></ListItemIcon><ListItemText primary={item} /></ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight="bold">{t('guide.priv_s2_title')}</Typography></AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" paragraph>{t('guide.priv_s2_intro')}</Typography>
              <List dense>
                {(t('guide.priv_s2_items', { returnObjects: true }) || []).map((item, idx) => (
                  <ListItem key={idx}><ListItemText primary={item} /></ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight="bold">{t('guide.priv_s3_title')}</Typography></AccordionSummary>
            <AccordionDetails>
              <List dense>
                {(t('guide.priv_s3_items', { returnObjects: true }) || []).map((item, idx) => (
                  <ListItem key={idx}><ListItemIcon><SecurityIcon fontSize="small" color="success" /></ListItemIcon><ListItemText primary={item} /></ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight="bold">{t('guide.priv_s4_title')}</Typography></AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" paragraph>{t('guide.priv_s4_intro')}</Typography>
              <List dense>
                {(t('guide.priv_s4_items', { returnObjects: true }) || []).map((item, idx) => (
                  <ListItem key={idx}><ListItemText primary={item} /></ListItem>
                ))}
              </List>
              <Typography variant="body2" color="text.secondary">{t('guide.priv_s4_note')}</Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight="bold">{t('guide.priv_s5_title')}</Typography></AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" paragraph>{t('guide.priv_s5_intro')}</Typography>
              <List dense>
                {(t('guide.priv_s5_items', { returnObjects: true }) || []).map((item, idx) => (
                  <ListItem key={idx}><ListItemText primary={item} /></ListItem>
                ))}
              </List>
              <Typography variant="body2" color="text.secondary">{t('guide.priv_s5_contact')}</Typography>
            </AccordionDetails>
          </Accordion>
          <Box sx={{ mt: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
            <Typography variant="body2">
              {t('guide.priv_last_updated')}<br />
              {t('guide.priv_update_note')}
            </Typography>
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 3, mt: 4, bgcolor: '#fff8e1' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <SupportAgentIcon fontSize="large" color="primary" />
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h6" fontWeight="bold">{t('guide.need_help')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('guide.label_phone')}: {COMPANY_INFO.phone}<br />
              LINE {t('guide.label_cs')}: {COMPANY_INFO.lineId}{t('guide.label_line_consult')}<br />
              {t('guide.label_email')}: {COMPANY_INFO.email}<br />
              {t('guide.label_service_hours')}: {COMPANY_INFO.serviceHours}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              {COMPANY_INFO.name}<br />
              {t('guide.label_tax_id')}: {COMPANY_INFO.taxId}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default ShoppingGuide;
